import { moneyFormatter } from '@wayste/client-utils';

/**
 * Options for formatting numbers
 */
export interface NumberFormatOptions {
    /** Format as currency (e.g., $1,234.56) */
    currency?: boolean;
    /** Use thousands separator (default: true) */
    thousandsSeparator?: boolean;
    /** Maximum number of decimal places */
    decimalPlaces?: number;
    /** Minimum number of decimal places */
    minDecimalPlaces?: number;
}

/**
 * Formats a number for display with optional currency and thousands separators
 *
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted string representation of the number
 *
 * @example
 * formatNumber(1234.56, { currency: true }) // "$1,234.56"
 * formatNumber(1234.56, { decimalPlaces: 0 }) // "1,235"
 * formatNumber(1234.5, { minDecimalPlaces: 2 }) // "1,234.50"
 */
export function formatNumber(value: number | undefined, options: NumberFormatOptions = {}): string {
    const { currency = false, thousandsSeparator = true, decimalPlaces, minDecimalPlaces } = options;

    // Handle undefined/NaN
    if (value === undefined || isNaN(value)) {
        return currency ? '$0.00' : '0';
    }

    // Use moneyFormatter for currency formatting
    if (currency) {
        return moneyFormatter(value, {
            isDollars: true,
            decimalPlaces: decimalPlaces ?? 2,
            hideDollarSign: false,
        });
    }

    // Non-currency formatting
    const formatOptions: Intl.NumberFormatOptions = {
        useGrouping: thousandsSeparator,
        minimumFractionDigits: minDecimalPlaces ?? 0,
        maximumFractionDigits: decimalPlaces ?? 20,
    };

    return new Intl.NumberFormat('en-US', formatOptions).format(value);
}

/**
 * Sanitizes numeric input by removing non-numeric characters
 * Allows: digits, decimal point, minus sign (at start only)
 *
 * @param input - The input string to sanitize
 * @param allowNegative - Whether to allow negative sign
 * @param allowDecimal - Whether to allow decimal point
 * @returns Sanitized string
 *
 * @example
 * sanitizeNumericInput("abc123.45def") // "123.45"
 * sanitizeNumericInput("-123.45abc") // "-123.45"
 */
export function sanitizeNumericInput(input: string, allowNegative = true, allowDecimal = true): string {
    let sanitized = input;

    // Remove all non-numeric characters except . and -
    sanitized = sanitized.replace(/[^\d.-]/g, '');

    // Only allow one decimal point
    if (allowDecimal) {
        const parts = sanitized.split('.');
        if (parts.length > 2) {
            sanitized = parts[0] + '.' + parts.slice(1).join('');
        }
    } else {
        sanitized = sanitized.replace(/\./g, '');
    }

    // Only allow minus at the start
    const hasLeadingNegative = allowNegative && sanitized.startsWith('-');
    sanitized = sanitized.replace(/-/g, '');
    if (hasLeadingNegative) {
        sanitized = '-' + sanitized;
    }

    return sanitized;
}
